import { Linear, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import * as PIXI from "pixi.js";
import { Application } from "../Application";
import {Anchor} from "../core/Anchor";
import {Ease} from "../core/Animatable";
import {Container} from "../core/Container";
import {array, Callback} from "../core/Reflection";
import {Sprite} from "../core/Sprite";
import {Keyboard} from "../gameobjects/Keyboard";
import { SplashScreenLayout } from "../layout/SplashScreenLayout";

export class LoadScreen extends HLF.LoadScreen {

    public static readonly NAME = "LoadScreen";

    public readonly bg = this.addChild(new PIXI.Graphics());
    public readonly cloudContainer = this.addChild(new PIXI.Container());
    public readonly cloud = array(() => this.cloudContainer.addChild(new Sprite()));
    public readonly earth = this.addChild(new Sprite());
    public readonly bee = this.addChild(new Sprite());
    public readonly ladybug = this.addChild(new Sprite());

    public readonly loaderEmpty = this.addChild(new Sprite());
    public readonly loaderFull = this.addChild(new Sprite());

    public loadingText?: PIXI.BitmapText;
    protected loaderMask = this.addChild(new PIXI.Graphics());

    private _onLoaded?: Callback;
    private _isAnimatedIn = false;
    private startTime: number = 0;

    public init(pSize: PIXI.Point) {
        this.bg.beginFill(0xC8E2F4, 1);
        this.bg.drawRect(0, 0, 2, 2);
        this.bg.endFill();
        this.bg.width = pSize.x;
        this.bg.height = pSize.y;
        SplashScreenLayout.call(this);

        this.loaderFull.position.x -= this.loaderFull.width * this.loaderFull.anchor.x;
        this.loaderFull.anchor.set(0, 0.5);

        this.loaderMask.beginFill(0xff0000, 1);
        this.loaderMask.drawRect(0, -this.loaderFull.height / 2, this.loaderFull.width, this.loaderFull.height);
        this.loaderMask.endFill();
        this.loaderMask.position.copyFrom(this.loaderFull.position);
        this.loaderMask.scale.x = 0;

        this.loaderFull.mask = this.loaderMask;

        this.loadingText = this.addChild(HLF.PixiUtils.makeText("Loading ...", "sesame", 36 * HLF.Application.instance.renderer.resolution, 0x24a4db));
        this.loadingText.align = "center";
        this.loadingText?.position.set((1024 / 2) + 10, this.loaderFull.y + 50);

        this._isAnimatedIn = false;
        this._onLoaded = undefined;

        Keyboard.clear();
        Keyboard.enabled = true;

        super.init(pSize);
        this._isAnimatedIn = true;
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        this.position.set((pSize.x - 1024) / 2, 0);
        this.bg.x = -(pSize.x - 1024) / 2;
        this.bg.width = pSize.x;
        this.bg.height = pSize.y;
    }

    public onLoadProgress(pLoader: PIXI.Loader | {progress: number}, pResource?: {name: string, url: string}, pLoaderIndex: number = 0): void {
        super.onLoadProgress(pLoader);

        let percent = pLoader.progress / 100 / 2;
        if (pLoaderIndex > 0) {
            percent += 0.5;
        }

        TweenLite.to(this.loaderMask, 0.25, {pixi: {
            scaleX: percent * 1,
        }, ease: Linear.easeNone});
    }

    public onLoadComplete(pCallback: () => void): void {
        this._onLoaded = pCallback;
        TweenLite.to(this.loaderMask, 0.25, {pixi: {
            scaleX: 1,
        }, ease: Linear.easeNone});
        this.hideLoader();
    }

    public animateIn(pOnComplete: () => void): void {
        this.startTime = new Date().valueOf();
        super.animateIn(pOnComplete);
    }
    public animateOut(pOnComplete: () => void): void {
        const now = new Date().valueOf();
        const minDuration = 500;
        const elapsed = Math.min(now - this.startTime);
        // hold on this screen for at least a second
        let delay = Math.max(0, minDuration - elapsed) / 1000;
        console.log("load complete after " + elapsed + "ms");
        console.log("waiting an additional " + delay + " seconds");

        // nothing to load = no callback, but animate the bar anyway
        if (!this._onLoaded) {
            TweenLite.to(this.loaderMask, 0.25, {pixi: {
                scaleX: 1,
            }});
            console.log("nothing was loaded");
            delay += 0.25;
        }

        TweenLite.delayedCall(delay, () => {
            if (Application.instance.paused) {
                console.warn("loading complete but game is paused");
                const _cb = () => {
                    PubSub.unsubscribe(_cb);
                    console.warn("game is unpaused, transitioning to next scene now");
                    super.animateOut(pOnComplete);
                };
                PubSub.subscribe(HLF.GAME_UNPAUSED, _cb);
            }
            else {
                super.animateOut(pOnComplete);
            }
        });
    }

    private hideLoader() {
        if (this._isAnimatedIn && this._onLoaded) {
            this.callOnLoaded();
        }
    }

    private callOnLoaded() {
        if (this._onLoaded) {
            this._onLoaded();
        }
    }
}
